import { DataStore } from './data-store';

export interface SearchResult {
  type: 'customer' | 'project' | 'invoice' | 'quote' | 'cost' | 'report' | 'visit' | 'image';
  id: string;
  title: string;
  subtitle?: string;
  value?: number;
  date?: string;
  status?: string;
  relevance: number; // 0-100
}

export interface SearchFilters {
  types?: string[];
  dateFrom?: string;
  dateTo?: string;
  minValue?: number;
  maxValue?: number;
  status?: string;
  customer?: string;
}

/**
 * Global search across all modules
 */
export const globalSearch = (query: string, filters?: SearchFilters): SearchResult[] => {
  const data = DataStore.getData();
  const results: SearchResult[] = [];
  const searchTerm = query.toLowerCase().trim();

  if (!searchTerm) return [];

  // Search Customers
  if (!filters?.types || filters.types.includes('customer')) {
    data.customers?.forEach(customer => {
      const relevance = calculateRelevance(
        searchTerm,
        [customer.nama, customer.email, customer.telefon, customer.alamat].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'customer',
          id: customer.id,
          title: customer.nama,
          subtitle: customer.telefon,
          value: undefined,
          date: customer.tarikhDaftar,
          status: customer.status,
          relevance,
        });
      }
    });
  }

  // Search Projects
  if (!filters?.types || filters.types.includes('project')) {
    data.projects?.forEach(project => {
      const relevance = calculateRelevance(
        searchTerm,
        [project.namaProjek, project.kodProjek, project.pelanggan, project.lokasi].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'project',
          id: project.id,
          title: project.namaProjek,
          subtitle: project.kodProjek,
          value: project.nilaiProjek,
          date: project.tarikh,
          status: project.status,
          relevance,
        });
      }
    });
  }

  // Search Invoices
  if (!filters?.types || filters.types.includes('invoice')) {
    data.invoices?.forEach(invoice => {
      const relevance = calculateRelevance(
        searchTerm,
        [invoice.noInvoice, invoice.projek, invoice.pelanggan].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'invoice',
          id: invoice.id,
          title: invoice.noInvoice,
          subtitle: invoice.pelanggan,
          value: invoice.total,
          date: invoice.tarikhInvoice,
          status: invoice.status,
          relevance,
        });
      }
    });
  }

  // Search Quotations
  if (!filters?.types || filters.types.includes('quote')) {
    data.quotations?.forEach(quote => {
      const relevance = calculateRelevance(
        searchTerm,
        [quote.no, quote.pelanggan, quote.namaProyek].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'quote',
          id: quote.id,
          title: quote.no,
          subtitle: quote.pelanggan,
          value: quote.jumlah,
          date: quote.tanggal,
          status: quote.status,
          relevance,
        });
      }
    });
  }

  // Search Costs
  if (!filters?.types || filters.types.includes('cost')) {
    data.costs?.forEach(cost => {
      const relevance = calculateRelevance(
        searchTerm,
        [cost.kodKos, cost.projek, cost.penerangan, cost.kategori].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'cost',
          id: cost.id,
          title: cost.kodKos,
          subtitle: cost.penerangan,
          value: cost.jumlah,
          date: cost.tarikh,
          relevance,
        });
      }
    });
  }

  // Search Reports
  if (!filters?.types || filters.types.includes('report')) {
    data.reports?.forEach(report => {
      const relevance = calculateRelevance(
        searchTerm,
        [report.kodLaporan, report.namaProyek, report.deskripsi].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'report',
          id: report.id,
          title: report.kodLaporan,
          subtitle: report.namaProyek,
          date: report.tanggalLaporan,
          relevance,
        });
      }
    });
  }

  // Search Site Visits
  if (!filters?.types || filters.types.includes('visit')) {
    data.siteVisits?.forEach(visit => {
      const relevance = calculateRelevance(
        searchTerm,
        [visit.no, visit.projek, visit.lokasi, visit.petugas].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'visit',
          id: visit.id,
          title: visit.no,
          subtitle: visit.projek,
          date: visit.tarikh,
          status: visit.status,
          relevance,
        });
      }
    });
  }

  // Search Images
  if (!filters?.types || filters.types.includes('image')) {
    data.projectImages?.forEach(image => {
      const relevance = calculateRelevance(
        searchTerm,
        [image.kodGambar, image.projek, image.keterangan].join(' ')
      );
      
      if (relevance > 0) {
        results.push({
          type: 'image',
          id: image.id,
          title: image.kodGambar,
          subtitle: image.keterangan,
          date: image.tarikh,
          relevance,
        });
      }
    });
  }

  // Apply filters
  let filtered = results;

  if (filters?.dateFrom) {
    filtered = filtered.filter(r => r.date && r.date >= filters.dateFrom!);
  }

  if (filters?.dateTo) {
    filtered = filtered.filter(r => r.date && r.date <= filters.dateTo!);
  }

  if (filters?.minValue !== undefined) {
    filtered = filtered.filter(r => r.value && r.value >= filters.minValue!);
  }

  if (filters?.maxValue !== undefined) {
    filtered = filtered.filter(r => r.value && r.value <= filters.maxValue!);
  }

  if (filters?.status) {
    filtered = filtered.filter(r => r.status === filters.status);
  }

  if (filters?.customer) {
    filtered = filtered.filter(r => r.subtitle?.includes(filters.customer!));
  }

  // Sort by relevance
  return filtered.sort((a, b) => b.relevance - a.relevance);
};

/**
 * Calculate relevance score for search results
 */
const calculateRelevance = (query: string, content: string): number => {
  const contentLower = content.toLowerCase();
  let score = 0;

  // Exact match
  if (contentLower === query) return 100;

  // Word match at start
  if (contentLower.startsWith(query)) score += 50;

  // Phrase match
  if (contentLower.includes(query)) score += 30;

  // Word by word match
  const queryWords = query.split(/\s+/);
  const contentWords = contentLower.split(/\s+/);
  
  queryWords.forEach(word => {
    if (contentWords.some(w => w.includes(word))) {
      score += 10;
    }
  });

  return Math.min(score, 100);
};

/**
 * Advanced filter builder
 */
export const buildFilterQuery = (filters: SearchFilters) => {
  const parts: string[] = [];

  if (filters.dateFrom) parts.push(`Date >= ${filters.dateFrom}`);
  if (filters.dateTo) parts.push(`Date <= ${filters.dateTo}`);
  if (filters.minValue) parts.push(`Value >= ${filters.minValue}`);
  if (filters.maxValue) parts.push(`Value <= ${filters.maxValue}`);
  if (filters.status) parts.push(`Status = ${filters.status}`);
  if (filters.customer) parts.push(`Customer = ${filters.customer}`);

  return parts.join(' AND ');
};

/**
 * Get search suggestions
 */
export const getSearchSuggestions = (query: string, limit: number = 10): string[] => {
  const data = DataStore.getData();
  const suggestions = new Set<string>();
  const searchTerm = query.toLowerCase();

  if (!searchTerm) return [];

  // Customer names
  data.customers?.forEach(c => {
    if (c.nama.toLowerCase().includes(searchTerm)) {
      suggestions.add(c.nama);
    }
  });

  // Project names
  data.projects?.forEach(p => {
    if (p.namaProjek.toLowerCase().includes(searchTerm)) {
      suggestions.add(p.namaProjek);
    }
  });

  // Invoice numbers
  data.invoices?.forEach(i => {
    if (i.noInvoice.toLowerCase().includes(searchTerm)) {
      suggestions.add(i.noInvoice);
    }
  });

  // Quotation numbers
  data.quotations?.forEach(q => {
    if (q.no.toLowerCase().includes(searchTerm)) {
      suggestions.add(q.no);
    }
  });

  return Array.from(suggestions).slice(0, limit);
};

/**
 * Export search results
 */
export const exportSearchResults = (results: SearchResult[], format: 'json' | 'csv' = 'json'): string => {
  if (format === 'json') {
    return JSON.stringify(results, null, 2);
  }

  // CSV format
  const headers = ['Type', 'ID', 'Title', 'Subtitle', 'Value', 'Date', 'Status', 'Relevance'];
  const rows = results.map(r => [
    r.type,
    r.id,
    r.title,
    r.subtitle || '',
    r.value || '',
    r.date || '',
    r.status || '',
    r.relevance
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  return csv;
};

/**
 * Get recently accessed items
 */
export const getRecentItems = (limit: number = 10): SearchResult[] => {
  const recentKey = 'sap_ngecat_recent_items';
  const stored = localStorage.getItem(recentKey);
  
  if (!stored) return [];

  try {
    const items = JSON.parse(stored) as SearchResult[];
    return items.slice(0, limit);
  } catch {
    return [];
  }
};

/**
 * Track item access for recents
 */
export const trackItemAccess = (item: SearchResult) => {
  const recentKey = 'sap_ngecat_recent_items';
  const stored = localStorage.getItem(recentKey);
  
  let items: SearchResult[] = [];
  if (stored) {
    try {
      items = JSON.parse(stored);
    } catch {
      items = [];
    }
  }

  // Remove if already exists
  items = items.filter(i => !(i.type === item.type && i.id === item.id));

  // Add to front
  items.unshift(item);

  // Keep only last 50
  items = items.slice(0, 50);

  localStorage.setItem(recentKey, JSON.stringify(items));
};

/**
 * Get statistics about searches
 */
export const getSearchStats = () => {
  const data = DataStore.getData();
  
  return {
    totalCustomers: data.customers?.length || 0,
    totalProjects: data.projects?.length || 0,
    totalInvoices: data.invoices?.length || 0,
    totalQuotations: data.quotations?.length || 0,
    totalCosts: data.costs?.length || 0,
    totalReports: data.reports?.length || 0,
    totalVisits: data.siteVisits?.length || 0,
    totalImages: data.projectImages?.length || 0,
    totalRecords: (
      (data.customers?.length || 0) +
      (data.projects?.length || 0) +
      (data.invoices?.length || 0) +
      (data.quotations?.length || 0) +
      (data.costs?.length || 0) +
      (data.reports?.length || 0) +
      (data.siteVisits?.length || 0) +
      (data.projectImages?.length || 0)
    )
  };
};
